import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Eye, Sparkles, Save, X, Calendar } from "lucide-react";

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [totalPosts, setTotalPosts] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [dailyBatchRunning, setDailyBatchRunning] = useState(false);
  const [backfillRunning, setBackfillRunning] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage, setPostsPerPage] = useState(50);
  
  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    content: "",
    excerpt: "",
    featured_image: "",
    seo_title: "",
    seo_description: "",
    seo_keywords: "",
    author: "Chatity Team",
    status: "draft",
    language: "en"
  });

  // AI Generation fields
  const [aiTopic, setAiTopic] = useState("");
  const [aiLanguage, setAiLanguage] = useState("en");
  const [aiTone, setAiTone] = useState("professional");

  useEffect(() => {
    fetchPosts();
  }, [currentPage, postsPerPage]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const offset = (currentPage - 1) * postsPerPage;
      const response = await axios.get(`${API}/admin/blog/posts?limit=${postsPerPage}&offset=${offset}`);
      setPosts(response.data.posts || []);
      setTotalPosts(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching blog posts:", error);
      toast.error("Failed to load blog posts");
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleTitleChange = (value) => {
    setFormData({
      ...formData,
      title: value,
      slug: generateSlug(value),
      seo_title: value
    });
  };

  const handleGenerateContent = async () => {
    if (!aiTopic.trim()) {
      toast.error("Please enter a topic");
      return;
    }

    try {
      setGenerating(true);
      const response = await axios.post(`${API}/admin/blog/generate`, {
        topic: aiTopic,
        language: aiLanguage,
        tone: aiTone
      });

      setFormData({
        ...formData,
        title: aiTopic,
        slug: generateSlug(aiTopic),
        content: response.data.content,
        excerpt: response.data.excerpt,
        seo_title: response.data.seo_title,
        seo_description: response.data.seo_description,
        seo_keywords: response.data.seo_keywords,
        language: aiLanguage
      });

      toast.success("Content generated successfully!");
      setShowEditor(true);
    } catch (error) {
      console.error("Error generating content:", error);
      toast.error("Failed to generate content");
    } finally {
      setGenerating(false);
    }
  };


  const handleBulkGenerate = async (count = 30) => {
    if (!window.confirm(`🤖 Generate ${count} blog posts automatically?\n\n- 15 in English\n- 15 in French\n- With SEO, images, and internal links\n\nThis will take 15-30 minutes.`)) {
      return;
    }

    try {
      setBulkGenerating(true);
      toast.loading(`Generating ${count} blog posts... This may take 15-30 minutes.`);
      
      const response = await axios.post(`${API}/admin/blog/generate-bulk?count=${count}`, {}, {
        timeout: 1800000 // 30 minutes timeout
      });

      toast.dismiss();
      
      if (response.data.success) {
        toast.success(`🎉 Successfully generated ${response.data.generated} blog posts!`);
        fetchPosts();
      } else {
        toast.error("Bulk generation failed");
      }
    } catch (error) {
      toast.dismiss();
      console.error("Error in bulk generation:", error);
      toast.error("Failed to generate posts: " + (error.response?.data?.detail || error.message));
    } finally {
      setBulkGenerating(false);
    }
  };


  const handleFixImages = async () => {
    if (!window.confirm("🖼️ Fix images for all blog posts?\n\nThis will update featured images to working URLs.")) {
      return;
    }

    try {
      toast.loading("Fixing images...");
      const response = await axios.post(`${API}/admin/blog/fix-images`);
      toast.dismiss();
      
      if (response.data.success) {
        toast.success(`✅ Fixed images for ${response.data.updated} posts!`);
        fetchPosts();
      }
    } catch (error) {
      toast.dismiss();
      console.error("Error fixing images:", error);
      toast.error("Failed to fix images");
    }
  };

  const handleDailyBatch = async () => {
    if (!window.confirm("🌍 Запустить генерацию мультиязычного контента?\n\n3 темы × 3 языка (EN/FR/RU) = 9 постов.\nLLM-вызовов: ~18. Время: 3-5 минут.")) {
      return;
    }
    try {
      setDailyBatchRunning(true);
      toast.loading("Генерация мультиязычного контента...");
      const response = await axios.post(`${API}/admin/blog/generate-daily-batch?themes_per_run=3`, {}, {
        timeout: 600000
      });
      toast.dismiss();
      const created = response.data?.created ?? response.data?.posts?.length ?? 0;
      toast.success(`✅ Создано ${created} постов (EN/FR/RU)`);
      fetchPosts();
    } catch (error) {
      toast.dismiss();
      toast.error("Ошибка: " + (error.response?.data?.detail || error.message));
    } finally {
      setDailyBatchRunning(false);
    }
  };

  const handleBackfillTranslations = async () => {
    if (!window.confirm("🔄 Дозаполнить переводы для старых постов?\n\nДля каждого поста без перевода создаст недостающие EN/FR/RU версии.\nЛимит: 50 тем за раз. Время: ~10 минут.")) {
      return;
    }
    try {
      setBackfillRunning(true);
      toast.loading("Backfill переводов...");
      const response = await axios.post(`${API}/admin/blog/backfill-translations?limit_themes=50&only_top_by_views=false`, {}, {
        timeout: 1800000
      });
      toast.dismiss();
      const created = response.data?.created ?? 0;
      toast.success(`✅ Backfill завершён, создано ${created} переводов`);
      fetchPosts();
    } catch (error) {
      toast.dismiss();
      toast.error("Ошибка: " + (error.response?.data?.detail || error.message));
    } finally {
      setBackfillRunning(false);
    }
  };

  const handleSavePost = async () => {
    if (!formData.title || !formData.slug || !formData.content) {
      toast.error("Title, slug, and content are required");
      return;
    }

    try {
      if (editingPost) {
        await axios.put(`${API}/admin/blog/posts/${editingPost.id}`, formData);
        toast.success("Post updated successfully!");
      } else {
        await axios.post(`${API}/admin/blog/posts`, formData);
        toast.success("Post created successfully!");
      }

      setShowEditor(false);
      setEditingPost(null);
      resetForm();
      fetchPosts();
    } catch (error) {
      console.error("Error saving post:", error);
      toast.error(error.response?.data?.detail || "Failed to save post");
    }
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      slug: post.slug,
      content: post.content,
      excerpt: post.excerpt || "",
      featured_image: post.featured_image || "",
      seo_title: post.seo_title || "",
      seo_description: post.seo_description || "",
      seo_keywords: post.seo_keywords || "",
      author: post.author || "Chatity Team",
      status: post.status,
      language: post.language
    });
    setShowEditor(true);
  };

  const handleDeletePost = async (postId) => {
    // More reliable confirmation - using toast for feedback
    const confirmed = window.confirm("⚠️ Are you sure you want to delete this post?\n\nThis action cannot be undone.");
    
    if (!confirmed) {
      return;
    }

    try {
      toast.loading("Deleting post...");
      await axios.delete(`${API}/admin/blog/posts/${postId}`);
      toast.dismiss();
      toast.success("✅ Post deleted successfully!");
      fetchPosts();
    } catch (error) {
      toast.dismiss();
      console.error("Error deleting post:", error);
      toast.error("❌ Failed to delete post: " + (error.response?.data?.detail || error.message));
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      slug: "",
      content: "",
      excerpt: "",
      featured_image: "",
      seo_title: "",
      seo_description: "",
      seo_keywords: "",
      author: "Chatity Team",
      status: "draft",
      language: "en"
    });
    setAiTopic("");
  };

  const handleCancel = () => {
    setShowEditor(false);
    setEditingPost(null);
    resetForm();
  };

  const contentRef = useRef(null);

  const insertHtmlTag = (openTag, closeTag = null) => {
    const textarea = contentRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = formData.content.substring(start, end);
    const closeTagToUse = closeTag || openTag.replace('<', '</');

    const newText = 
      formData.content.substring(0, start) +
      openTag + selectedText + closeTagToUse +
      formData.content.substring(end);

    setFormData({...formData, content: newText});

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        start + openTag.length,
        start + openTag.length + selectedText.length
      );
    }, 0);
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {!showEditor ? (
        <>
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h2 className="text-3xl font-bold">Blog Management</h2>
              <p className="text-gray-600 mt-1">Total: {totalPosts} posts</p>
            </div>
            <div className="flex gap-3 flex-wrap">
              <Button 
                onClick={handleDailyBatch}
                disabled={dailyBatchRunning}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:from-emerald-700 hover:to-teal-700"
                data-testid="generate-daily-batch-btn"
                title="3 темы × 3 языка (EN/FR/RU) = 9 постов"
              >
                {dailyBatchRunning ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Генерация...
                  </>
                ) : (
                  <>🌍 Мультиязычная генерация (3×3)</>
                )}
              </Button>
              <Button 
                onClick={handleBackfillTranslations}
                disabled={backfillRunning}
                className="bg-indigo-600 text-white hover:bg-indigo-700"
                data-testid="backfill-translations-btn"
                title="Дозаполнить переводы для старых постов"
              >
                {backfillRunning ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Backfill...
                  </>
                ) : (
                  <>🔄 Backfill переводов</>
                )}
              </Button>
              <Button 
                onClick={() => handleBulkGenerate(30)}
                disabled={bulkGenerating}
                className="bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700"
              >
                {bulkGenerating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Auto-Generate 30 Posts
                  </>
                )}
              </Button>
              <Button 
                onClick={handleFixImages}
                className="bg-orange-600 text-white hover:bg-orange-700"
                title="Fix broken images in all posts"
              >
                🖼️ Fix Images
              </Button>
              <Button 
                onClick={() => setShowEditor(true)}
                className="bg-black text-white hover:bg-gray-800"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Post
              </Button>
            </div>
          </div>

          {/* Pagination Controls */}
          <div className="flex justify-between items-center mb-6 p-4 bg-gray-50 rounded-lg border-2 border-black text-black">
            <div className="flex items-center gap-2">
              <Label className="font-bold text-black">Posts per page:</Label>
              <select
                value={postsPerPage}
                onChange={(e) => {
                  setPostsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-3 py-2 border-2 border-black rounded-lg font-bold text-black bg-white"
              >
                <option value={10}>10</option>
                <option value={50}>50</option>
                <option value={300}>300</option>
              </select>
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-black">
                Page {currentPage} of {Math.ceil(totalPosts / postsPerPage) || 1}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="border-2 border-black font-bold text-black"
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => p + 1)}
                disabled={currentPage >= Math.ceil(totalPosts / postsPerPage)}
                className="border-2 border-black font-bold text-black"
              >
                Next
              </Button>
            </div>
          </div>

          {/* Posts List */}
          {posts.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-gray-500">
                No blog posts yet. Create your first post!
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3">
              {posts.map((post) => (
                <Card key={post.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-center">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-bold">{post.title}</h3>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            post.status === 'published' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {post.status}
                          </span>
                          <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            {post.language.toUpperCase()}
                          </span>
                          <span className="text-xs text-gray-500">
                            <Calendar className="w-3 h-3 inline mr-1" />
                            {new Date(post.created_at).toLocaleDateString()}
                          </span>
                          <span className="text-xs text-gray-500">
                            <Eye className="w-3 h-3 inline mr-1" />
                            {post.views_count || 0}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {post.status === 'published' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(`/blog/${post.slug}`, '_blank')}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditPost(post)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeletePost(post.id);
                          }}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 border-2 border-red-600"
                          title="Delete post"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          {/* Editor Header */}
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-3xl font-bold">
              {editingPost ? 'Edit Post' : 'New Post'}
            </h2>
            <div className="flex gap-2">
              <Button 
                onClick={handleCancel}
                variant="outline"
                className="border-2 border-black"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button 
                onClick={handleSavePost}
                className="bg-black text-white hover:bg-gray-800"
              >
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
            </div>
          </div>

          {/* AI Content Generator */}
          {!editingPost && (
            <Card className="mb-6 border-2 border-black">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  AI Content Generator
                </CardTitle>
                <CardDescription>
                  Generate a complete blog post using GPT-5
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4">
                  <div>
                    <Label>Topic</Label>
                    <Input
                      value={aiTopic}
                      onChange={(e) => setAiTopic(e.target.value)}
                      placeholder="e.g., How AI Chatbots Transform Customer Service"
                      className="border-2 border-black"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Language</Label>
                      <select
                        value={aiLanguage}
                        onChange={(e) => setAiLanguage(e.target.value)}
                        className="w-full p-2 border-2 border-black rounded-lg"
                      >
                        <option value="en">English</option>
                        <option value="fr">Français</option>
                      </select>
                    </div>
                    <div>
                      <Label>Tone</Label>
                      <select
                        value={aiTone}
                        onChange={(e) => setAiTone(e.target.value)}
                        className="w-full p-2 border-2 border-black rounded-lg"
                      >
                        <option value="professional">Professional</option>
                        <option value="casual">Casual</option>
                        <option value="technical">Technical</option>
                      </select>
                    </div>
                  </div>
                  <Button 
                    onClick={handleGenerateContent}
                    disabled={generating}
                    className="bg-black text-white hover:bg-gray-800"
                  >
                    {generating ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Generate Content
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Post Editor */}
          <div className="grid gap-6">
            {/* Basic Info */}
            <Card>
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4">
                <div>
                  <Label>Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    placeholder="Post title"
                    className="border-2 border-black"
                  />
                </div>
                <div>
                  <Label>Slug *</Label>
                  <Input
                    value={formData.slug}
                    onChange={(e) => setFormData({...formData, slug: e.target.value})}
                    placeholder="post-url-slug"
                    className="border-2 border-black"
                  />
                  <p className="text-sm text-gray-500 mt-1">URL: /blog/{formData.slug}</p>
                </div>
                <div>
                  <Label>Excerpt</Label>
                  <textarea
                    value={formData.excerpt}
                    onChange={(e) => setFormData({...formData, excerpt: e.target.value})}
                    placeholder="Brief summary of the post"
                    className="w-full p-2 border-2 border-black rounded-lg min-h-[80px]"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Language</Label>
                    <select
                      value={formData.language}
                      onChange={(e) => setFormData({...formData, language: e.target.value})}
                      className="w-full p-2 border-2 border-black rounded-lg"
                    >
                      <option value="en">English</option>
                      <option value="fr">Français</option>
                    </select>
                  </div>
                  <div>
                    <Label>Status</Label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({...formData, status: e.target.value})}
                      className="w-full p-2 border-2 border-black rounded-lg"
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                    </select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Content */}
            <Card>
              <CardHeader>
                <CardTitle>Content *</CardTitle>
                <CardDescription>
                  HTML editor - select text and use formatting buttons
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Formatting Toolbar */}
                <div className="flex flex-wrap gap-2 p-2 bg-gray-50 border-2 border-black rounded-lg">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => insertHtmlTag('<h2>', '</h2>')}
                    className="font-bold"
                  >
                    H2
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => insertHtmlTag('<h3>', '</h3>')}
                    className="font-bold"
                  >
                    H3
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => insertHtmlTag('<strong>', '</strong>')}
                    className="font-bold"
                  >
                    <strong>B</strong>
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => insertHtmlTag('<em>', '</em>')}
                    className="italic"
                  >
                    <em>I</em>
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => insertHtmlTag('<p>', '</p>')}
                  >
                    P
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => insertHtmlTag('<ul>\n  <li>', '</li>\n</ul>')}
                  >
                    • List
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => insertHtmlTag('<ol>\n  <li>', '</li>\n</ol>')}
                  >
                    1. List
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => insertHtmlTag('<a href="">', '</a>')}
                  >
                    Link
                  </Button>
                </div>

                {/* HTML Textarea */}
                <textarea
                  ref={contentRef}
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  placeholder="Write your content in HTML format..."
                  className="w-full p-4 border-2 border-black rounded-lg font-mono text-sm"
                  style={{ minHeight: '400px' }}
                />
              </CardContent>
            </Card>

            {/* SEO */}
            <Card>
              <CardHeader>
                <CardTitle>SEO Settings</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4">
                <div>
                  <Label>SEO Title</Label>
                  <Input
                    value={formData.seo_title}
                    onChange={(e) => setFormData({...formData, seo_title: e.target.value})}
                    placeholder="SEO optimized title (60 chars max)"
                    className="border-2 border-black"
                    maxLength={60}
                  />
                  <p className="text-sm text-gray-500 mt-1">{formData.seo_title.length}/60</p>
                </div>
                <div>
                  <Label>Meta Description</Label>
                  <textarea
                    value={formData.seo_description}
                    onChange={(e) => setFormData({...formData, seo_description: e.target.value})}
                    placeholder="Meta description (160 chars max)"
                    className="w-full p-2 border-2 border-black rounded-lg"
                    maxLength={160}
                  />
                  <p className="text-sm text-gray-500 mt-1">{formData.seo_description.length}/160</p>
                </div>
                <div>
                  <Label>Keywords</Label>
                  <Input
                    value={formData.seo_keywords}
                    onChange={(e) => setFormData({...formData, seo_keywords: e.target.value})}
                    placeholder="keyword1, keyword2, keyword3"
                    className="border-2 border-black"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
